import { useState, type FormEvent } from "react";
import { Send } from "lucide-react";
import { Field } from "@/components/forms";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import type { SupportTicket } from "@/hooks/useSupport";

const formatDateTime = (value: string) =>
  new Date(value).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

interface Entry {
  key: string;
  author: string;
  staff: boolean;
  body: string;
  at: string;
}

/** A ticket's description followed by every reply, oldest first, with a reply box. */
export function TicketConversation({
  ticket,
  viewer,
  onReply,
  replying,
  replyHint,
}: {
  ticket: SupportTicket;
  /** Whose side "you" is on, so the right bubbles read as the viewer's own. */
  viewer: "requester" | "staff";
  onReply: (body: string) => Promise<unknown>;
  replying: boolean;
  replyHint?: string;
}) {
  const [draft, setDraft] = useState("");

  const entries: Entry[] = [
    {
      key: "description",
      author: ticket.user_name || "Requester",
      staff: false,
      body: ticket.description,
      at: ticket.created_at,
    },
    ...(ticket.messages ?? []).map((m) => ({
      key: String(m.id),
      author: m.is_staff ? m.author_name || "Taskpart support" : m.author_name || "Requester",
      staff: m.is_staff,
      body: m.body,
      at: m.created_at,
    })),
  ];

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const body = draft.trim();
    if (!body) return;
    try {
      await onReply(body);
      setDraft("");
    } catch {
      // The API client already shows the error; keep the draft so nothing is lost.
    }
  };

  return (
    <div className="space-y-5">
      <ol className="space-y-3" aria-label="Conversation">
        {entries.map((entry) => {
          const own = viewer === "staff" ? entry.staff : !entry.staff;
          return (
            <li key={entry.key} className={cn("flex", own ? "justify-end" : "justify-start")}>
              <div
                className={cn(
                  "max-w-[88%] rounded-md border px-3.5 py-2.5",
                  own ? "border-border bg-muted" : "border-border bg-card",
                )}
              >
                <p className="text-caption text-muted-foreground">
                  <span className="font-medium text-foreground">{own ? "You" : entry.author}</span>
                  {entry.staff && !own && " · Support"} ·{" "}
                  <span className="tabular-nums">{formatDateTime(entry.at)}</span>
                </p>
                <p className="mt-1 text-sm leading-6 whitespace-pre-wrap text-body">{entry.body}</p>
              </div>
            </li>
          );
        })}
      </ol>

      <form onSubmit={submit} className="space-y-2">
        <Field label="Reply" hint={replyHint}>
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={4}
            maxLength={5000}
            placeholder="Write a reply…"
          />
        </Field>
        <div className="flex justify-end">
          <Button type="submit" loading={replying} disabled={!draft.trim()}>
            <Send />
            Send reply
          </Button>
        </div>
      </form>
    </div>
  );
}

export { formatDateTime };
